import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  FormControl,
  TextField,
  Stack,
  InputAdornment,
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  CircularProgress,
  Fade,
  useMediaQuery,
  Divider,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { gql, useQuery } from "@apollo/client";
import AddIngredients from "../inventory/AddIngredients";
import { useNotification } from "../../Context/notificationContext";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import GrainIcon from "@mui/icons-material/Grain";
import ScaleIcon from "@mui/icons-material/Scale";

// query of get ingredients
export const GET_INGREDIENTS = gql`
  query GetIngredients($ingredientType: IngredientType) {
    getIngredients(ingredientType: $ingredientType) {
      id
      ingredient_name
      ingredient_type
      created_by
      created
      modified
      image_url
    }
  }
`;

export default function ChooseGrainsForm({ task, onSubmit }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selectedItem, setSelectedItem] = useState(task.item_name || "");
  const [quantity, setQuantity] = useState(task.quantity || "");
  const [error, setError] = useState("");
  const [itemError, setItemError] = useState("");
  const { showNotification } = useNotification();
  const minQuantity = 10;
  const maxQuantity = 500;

  const handleInputChange = (value) => {
    setSelectedItem(value);
  };

  const {
    data,
    loading: getIngredientsLoading,
    error: getIngredientsError,
    refetch: refetchIngredients,
  } = useQuery(GET_INGREDIENTS, {
    variables: { ingredientType: "GRAIN" },
    fetchPolicy: "cache-and-network",
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validateForm()) {
      task.args = selectedItem + " - " + quantity + "g";
      task.quantity = quantity;
      task.item_name = selectedItem;
      onSubmit(task);
    }
  };

  const validateForm = () => {
    if (!selectedItem) {
      setItemError("Please select a grain");
      setError("");
      return false;
    }
    if (+quantity < minQuantity || +quantity > maxQuantity) {
      setError(`Quantity must be between ${minQuantity} and ${maxQuantity} grams`);
      setItemError("");
      return false;
    }
    setError("");
    setItemError("");
    return true;
  };

  const [addIngredientActive, setAddIngredientActive] = useState(false);
  const [item_name, setItem_name] = useState("");
  const [ingredientType, setIngredientType] = useState(null);
  const [ingredientImage, setIngredientImage] = useState(null);
  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    if (data && data.getIngredients) {
      setIngredients(data?.getIngredients);
    }

    if (getIngredientsError) {
      showNotification("error", getIngredientsError.message);
    }
  }, [data, getIngredientsError, showNotification]);

  useEffect(() => {
    if (task?.item_name !== "") {
      setSelectedItem(task.item_name);
    }
  }, [task?.item_name]);

  return addIngredientActive ? (
    <AddIngredients
      newIngredientName={item_name}
      setNewIngredientName={setItem_name}
      ingredientType={ingredientType}
      setIngredientType={setIngredientType}
      ingredientImage={ingredientImage}
      setIngredientImage={setIngredientImage}
      ingredients={ingredients}
      setIngredients={setIngredients}
      setAddIngredientActive={setAddIngredientActive}
      refetchIngredients={refetchIngredients}
    />
  ) : (
    <Fade in={true} timeout={500}>
      <Paper 
        elevation={3} 
        sx={{
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 2,
          background: alpha(theme.palette.background.paper, 0.9),
          backdropFilter: "blur(10px)",
          transition: "all 0.3s ease-in-out",
          maxWidth: "600px",
          margin: "0 auto",
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight="bold" color="primary">
            <GrainIcon sx={{ mr: 1, verticalAlign: "text-bottom" }} />
            Choose Grain
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Select a grain and specify the quantity
          </Typography>
          <Divider sx={{ mt: 2 }} />
        </Box>

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Autocomplete
              options={data?.getIngredients || []}
              getOptionLabel={(option) => option.ingredient_name || ""}
              loading={getIngredientsLoading}
              filterOptions={(options, { inputValue }) =>
                options.filter((option) =>
                  option.ingredient_name
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Grain"
                  variant="outlined"
                  error={!!itemError}
                  helperText={itemError}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <GrainIcon color="primary" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {getIngredientsLoading ? (
                          <CircularProgress color="primary" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 1.5,
                    borderRadius: 1,
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      overflow: "hidden",
                      flexShrink: 0,
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    }}
                  >
                    <img
                      src={option.image_url || "/placeholder-16x9.png"}
                      alt={option.ingredient_name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      loading="lazy"
                    />
                  </Box>
                  <Typography variant="body1">{option.ingredient_name}</Typography>
                </Box>
              )}
              noOptionsText={
                getIngredientsLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography>Loading ingredients...</Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 1.5, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Ingredient not found in the list
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={() => setAddIngredientActive(true)}
                      size="medium"
                    >
                      Add New Grain
                    </Button>
                  </Box>
                )
              }
              value={ingredients.find((item) => item.ingredient_name === selectedItem) || null}
              onInputChange={(event, newInputValue) => handleInputChange(newInputValue)}
              fullWidth
              disabled={getIngredientsLoading || !data?.getIngredients?.length}
              sx={{
                "& .MuiOutlinedInput-root": {
                  transition: "all 0.3s",
                  "&:hover": {
                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                  "&.Mui-focused": {
                    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
                  },
                },
              }}
            />

            <FormControl fullWidth variant="outlined">
              <TextField
                label="Grain Quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                error={!!error}
                helperText={error || `Range: ${minQuantity} - ${maxQuantity} grams`}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ScaleIcon color="primary" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography color="text.secondary">grams</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    transition: "all 0.3s",
                    "&:hover": {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
                    },
                    "&.Mui-focused": {
                      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.3)}`,
                    },
                  },
                }}
              />
            </FormControl>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              size={isMobile ? "large" : "medium"}
              sx={{
                py: 1.5,
                fontWeight: "bold",
                borderRadius: 2,
                boxShadow: theme.shadows[4],
                transition: "all 0.3s",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: theme.shadows[6],
                },
              }}
            >
              Submit Selection
            </Button>
          </Stack>
        </form>
      </Paper>
    </Fade>
  );
}

ChooseGrainsForm.propTypes = {
  task: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
